// Hook that guards against accidentally leaving a page with unsaved changes.
// Wire it up in any Visual Editor (or form) that tracks a `dirty` boolean —
// while dirty, it will:
//   * intercept clicks on any anchor (`<Link>` renders one) with a native
//     confirm dialog. Cancelling stays; confirming lets the click proceed.
//   * install a `beforeunload` listener so browser tab close / reload /
//     back-button also show the browser's own leave prompt.
//   * patch history.pushState/replaceState + popstate so programmatic
//     navigation is also guarded.
//
// Message can be customised; the default matches the visual editors.
import { useEffect } from "react";

const DEFAULT_MESSAGE =
  "You have unsaved changes in the editor. Leave without saving?";

export function useUnsavedGuard(dirty, message = DEFAULT_MESSAGE) {
  useEffect(() => {
    if (!dirty) return;

    // Set for a brief window whenever the user just confirmed a navigation
    // via anchor click — prevents the subsequent history.pushState from
    // React Router firing a second identical prompt.
    let skipUntil = 0;

    // 1. Intercept anchor clicks anywhere in the document. React Router's
    //    <Link> renders a real <a href>, so this catches nav links too.
    const onClick = (e) => {
      // Only care about primary-button, unmodified clicks.
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest && e.target.closest("a[href]");
      if (!anchor) return;
      // Ignore external targets, downloads, and same-page hash links.
      if (anchor.target && anchor.target !== "" && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      // Skip mailto:, tel:, javascript: schemes.
      if (/^(mailto:|tel:|javascript:)/i.test(href)) return;
      // Same-URL clicks aren't navigation.
      const target = new URL(anchor.href, window.location.href);
      if (target.pathname === window.location.pathname
          && target.search === window.location.search
          && target.hash === window.location.hash) return;
      if (window.confirm(message)) {
        // Let the click through — but tell pushState guard to skip once.
        skipUntil = Date.now() + 500;
      } else {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    // Capture phase so we run BEFORE React Router's own click handler.
    document.addEventListener("click", onClick, true);

    // 2. Browser-level guard for tab close, hard reload, external link.
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    // 3. Guard programmatic history navigation (e.g. useNavigate() calls
    //    that aren't triggered by a click we already prompted for).
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    const guardedNav = (fn) => (...args) => {
      if (Date.now() < skipUntil) return fn.apply(window.history, args);
      const nextUrl = args[2];
      if (nextUrl != null) {
        const target = new URL(nextUrl, window.location.href);
        const same = target.pathname === window.location.pathname
                  && target.search === window.location.search;
        if (!same && !window.confirm(message)) return;
      }
      return fn.apply(window.history, args);
    };
    window.history.pushState = guardedNav(origPush);
    window.history.replaceState = guardedNav(origReplace);

    // Back/forward button — popstate fires AFTER the URL has changed, so if
    // the user cancels we push the previous URL back onto the stack.
    const onPopState = (e) => {
      if (!window.confirm(message)) {
        origPush.call(window.history, e.state, "", document.location.href);
      }
    };
    window.addEventListener("popstate", onPopState);

    return () => {
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("popstate", onPopState);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, [dirty, message]);
}
